import { getEnvColorById } from '@resistapp/client/components/shared/palettes';
import { AbunanceSelection, Filters } from '@resistapp/client/data-utils/filter-data/filter';
import { AbundanceStats } from '@resistapp/common/api-types';
import { GeneGrouping, GetGroup, L2Target, L2Targets, sixteenS } from '@resistapp/common/assays';
import { getBioRepGroupKey, getSimpleBioNumber } from '@resistapp/common/environments';
import { buildSortIdxByGene } from '@resistapp/common/export-utils';
import { getAbundanceLog10Stats } from '@resistapp/common/statistics/abundance-stats';
import {
  Abundance,
  CtsColumns,
  Environment,
  FullAbundance,
  FullProject,
  FullSample,
  FullSamplesByUID,
  NormalisationMode,
  XAxisMode,
} from '@resistapp/common/types';
import {
  TRACES_VAL,
  flattenRelevantAbundances,
  flattenSamplesByUID,
  getBioGroupSortValue,
  getDateStrForReplicatedSample,
  getSampleSortValue,
} from '@resistapp/common/utils';
import { sum } from 'd3-array';
import { Dictionary, flatten, groupBy, keyBy, keys, mapValues, sortBy, uniq, uniqBy, values } from 'lodash';
import { getNormalisedValue } from '../../../common/normalisation-mode';
import { ExperimentalMetrics } from './build-experimental-metrics';

export type BarOrBoxPlotData = BoxPlotData | BarPlotData;

export type SingleBoxPlotSeries = BoxDatum[];
export type SingleBarPlotSeries = BarDatum[];

export type BoxPlotData = SingleBoxPlotSeries[];
export type BarPlotData = SingleBarPlotSeries[];

export interface DateDatum {
  date: string;
  label: string;
}

export interface PlotDatum extends DateDatum {
  color: string;
  sampleId: string;
  sampleNumber: string;
  bioNumber: string;
  environmentId: number;
}

export interface BoxDatum extends PlotDatum, AbundanceStats {
  experimentalMetrics?: ExperimentalMetrics;
}

export interface BarDatum extends PlotDatum {
  ctsByAssay?: Record<string, CtsColumns[]>;
  measuredByGene?: Record<string, boolean>;
  [key: string]: string | number | Record<string, CtsColumns[]> | Record<string, boolean> | undefined;
}

export interface ResearchPlotData {
  againstTime: boolean; // legacy, to be deprecated, use xAxisMode instead
  grouping: GeneGrouping;
  geneGroups: string[];
  numAnalyzed: number;
  normalisationMode: NormalisationMode;
  boxData?: BoxPlotData; // array of box series (ie. groups)
  barData?: BarPlotData; // array of bar series (ie. groups) of dicts keyd by date and targets / genes
  heatData?: BarPlotData; // array of bar series (grouped, coinciding and bio samples smashed together) of dicts keyd by date and targets / genes
  sortIdxByGene: Dictionary<number>;
}

export type PartialFilters = Pick<
  Filters,
  | 'selectedTargets'
  | 'selectedTargetGrouping'
  | 'normalisationMode'
  | 'abundances'
  | 'selectedEnvironmentIdsOrdered'
  | 'xAxisMode'
>;

export function buildResearchPlotData(
  project: FullProject,
  filteringOptions: PartialFilters,
  allGeneGroups: Record<string, Array<L2Targets | string>>,
  getGroup: GetGroup,
): ResearchPlotData | null {
  // Note: filtering is already applied
  const maybeFocusedByBioGroupKey = project.focusedByUID || project.samplesByUID;
  const {
    selectedTargets,
    selectedTargetGrouping,
    normalisationMode,
    abundances: abundanceSelection,
    selectedEnvironmentIdsOrdered,
    xAxisMode,
  } = filteringOptions;

  // Assume all samples have time and pick one time for replicated samples
  const dateByBioGroupKey = mapValues(maybeFocusedByBioGroupKey, getDateStrForReplicatedSample);

  const envColorById = getEnvColorById(project);
  const allSamples = flattenSamplesByUID(project.samplesByUID);
  if (!allSamples.length) {
    // Legacy projects
    return null;
  }
  const allRelAbundances = flattenRelevantAbundances(allSamples, selectedTargetGrouping === sixteenS);
  const relevantRelAbundances = allRelAbundances.filter(datum => getGroup(datum.assay, selectedTargetGrouping));
  const unsortedGeneGroups = uniq(relevantRelAbundances.map(datum => getGroup(datum.assay, selectedTargetGrouping)));
  const geneGroups = allGeneGroups[selectedTargetGrouping]
    .filter(g => unsortedGeneGroups.includes(g as L2Target))
    .reverse();
  const enabledGeneGroups = geneGroups.filter(t => selectedTargets.includes(t as L2Target));
  const numAnalyzed = uniqBy(relevantRelAbundances, datum => datum.assay).filter(datum =>
    enabledGeneGroups.includes(getGroup(datum.assay, selectedTargetGrouping) as string),
  ).length;

  const maybeFocusedSamples = flattenSamplesByUID(maybeFocusedByBioGroupKey);
  const sortIdxByGene = buildSortIdxByGene(allSamples, selectedTargetGrouping, getGroup, maybeFocusedSamples);
  const showTraces = abundanceSelection !== AbunanceSelection.QUANTIFIED_ONLY;

  switch (xAxisMode) {
    case XAxisMode.TIME: {
      if (!values(dateByBioGroupKey).every(Boolean)) {
        // Samples without time not supported in TIME mode
        return null;
      }
      // Plot against time iff some environment is sampled (non-replicatedly) on different times.
      // Plotting code assumes that box groups are from the same time point.

      const groupedByTime = groupBy(maybeFocusedSamples, sample => dateByBioGroupKey[getBioRepGroupKey(sample)]);
      const data = values(groupedByTime).map(timeSamples => {
        const { boxData, barData, heatData } = getBarAndBoxDataForCoincidingSamplesByEnv(
          timeSamples,
          normalisationMode,
          dateByBioGroupKey as Dictionary<string>,
          selectedTargetGrouping,
          geneGroups,
          showTraces,
          abundanceSelection === AbunanceSelection.ESTIMATES,
          abundanceSelection === AbunanceSelection.MEASURED_OR_ESTIMATE,
          envColorById,
          selectedEnvironmentIdsOrdered,
          project.id,
          getGroup,
        );
        return {
          boxData,
          barData,
          heatData: flatten(heatData),
        };
      });
      return {
        againstTime: true,
        geneGroups,
        numAnalyzed,
        grouping: selectedTargetGrouping,
        normalisationMode,
        boxData: data.map(d => d.boxData),
        barData: data.map(d => d.barData),
        heatData: data.map(d => d.heatData),
        sortIdxByGene,
      };
    }
    case XAxisMode.ORDINAL_GROUPED: {
      // Build one plot group per BioRep UID (environment + time).

      const orderedBioGroupKeys = keys(maybeFocusedByBioGroupKey).sort((uidA, uidB) => {
        const samplesA = maybeFocusedByBioGroupKey[uidA];
        const samplesB = maybeFocusedByBioGroupKey[uidB];

        const envA = samplesA[0].environment.id;
        const envB = samplesB[0].environment.id;

        // 1. Environment order - honour query parameter order if given
        if (selectedEnvironmentIdsOrdered.length) {
          const idxA = selectedEnvironmentIdsOrdered.indexOf(envA);
          const idxB = selectedEnvironmentIdsOrdered.indexOf(envB);
          if (idxA !== idxB) return idxA - idxB;
        } else if (envA !== envB) {
          return envA - envB;
        }

        // 2. Project insertion order within environment bio replicas
        return getBioGroupSortValue(samplesA, project.id) - getBioGroupSortValue(samplesB, project.id);
        // NOTE: this might not be that meaningful if we start copying and moving samples.
        // An alternative would be to use sampling date:
        // const timeA = dateByBioGroupKey[uidA] || '';
        // const timeB = dateByBioGroupKey[uidB] || '';
        // if (timeA !== timeB) return timeA.localeCompare(timeB);
      });

      const boxData: BoxPlotData = [];
      const barData: BarPlotData = [];
      const heatData: BarPlotData = [];

      orderedBioGroupKeys.forEach(bioGroupKey => {
        const groupSamples = maybeFocusedByBioGroupKey[bioGroupKey];
        const environment = groupSamples[0].environment;
        const time = dateByBioGroupKey[bioGroupKey] || '';
        const focusedAbundances = flattenRelevantAbundances(groupSamples, selectedTargetGrouping === sixteenS);

        // Box (one datum) inside series
        boxData.push([
          buildBoxDatum(
            environment,
            focusedAbundances,
            normalisationMode,
            envColorById,
            time,
            String(groupSamples[0].number),
            project.id,
            groupSamples[0],
            getGroup,
          ),
        ]);

        // Detected genes bar (one stacked bar) inside series
        barData.push([
          buildBarDatum(
            environment,
            selectedTargetGrouping,
            geneGroups,
            focusedAbundances,
            time,
            envColorById,
            String(groupSamples[0].number),
            project.id,
            groupSamples[0],
            getGroup,
          ),
        ]);

        // Heat series (one column per bio rep sample)
        heatData.push(
          buildSingleHeatMapSeries(
            groupSamples,
            selectedTargetGrouping,
            normalisationMode,
            showTraces,
            abundanceSelection === AbunanceSelection.ESTIMATES,
            abundanceSelection === AbunanceSelection.MEASURED_OR_ESTIMATE,
            time,
            envColorById,
            project.id,
          ),
        );
      });

      return {
        againstTime: false,
        geneGroups,
        numAnalyzed,
        grouping: selectedTargetGrouping,
        normalisationMode,
        boxData,
        barData,
        heatData,
        sortIdxByGene,
      };
    }
    case XAxisMode.ORDINAL_INDIVIDUAL: {
      // Preserve the exact project insertion order for the INDIVIDUAL x-axis mode.
      // Using only the project sample id (via getSampleSortValue) avoids any additional
      // grouping logic – such as environment or bio-rep – that would otherwise
      // reorder the data. This guarantees 1A, 2A, … 8A, 1B, 2B … just as they were
      // added to the project.

      // NOTE: This does not respect selection order!
      const orderedSamples = sortBy(maybeFocusedSamples, s => getSampleSortValue(s, project.id));

      const boxData: BoxPlotData = [];
      const barData: BarPlotData = [];
      const heatData: BarPlotData = [];

      orderedSamples.forEach(sample => {
        const abundances = flattenRelevantAbundances([sample], selectedTargetGrouping === sixteenS);
        const time = sample.time ? sample.time.toISOString() : '';
        boxData.push([
          buildBoxDatum(
            sample.environment,
            abundances,
            normalisationMode,
            envColorById,
            time,
            String(sample.number),
            project.id,
            sample,
            getGroup,
          ),
        ]);
        barData.push([
          buildBarDatum(
            sample.environment,
            selectedTargetGrouping,
            geneGroups,
            abundances,
            time,
            envColorById,
            String(sample.number),
            project.id,
            sample,
            getGroup,
          ),
        ]);
        heatData.push(
          buildSingleHeatMapSeries(
            [sample],
            selectedTargetGrouping,
            normalisationMode,
            showTraces,
            abundanceSelection === AbunanceSelection.ESTIMATES,
            abundanceSelection === AbunanceSelection.MEASURED_OR_ESTIMATE,
            time,
            envColorById,
            project.id,
          ),
        );
      });
      return {
        againstTime: false,
        geneGroups,
        numAnalyzed,
        grouping: selectedTargetGrouping,
        normalisationMode,
        boxData,
        barData,
        heatData,
        sortIdxByGene,
      };
    }
    default:
      return null;
  }
}

export function getBarDatumKeys(datum: BarDatum) {
  return keys(datum).filter(
    key =>
      key !== 'date' &&
      key !== 'label' &&
      key !== 'color' &&
      key !== 'sampleNumber' &&
      key !== 'sampleId' &&
      key !== 'environmentId' &&
      key !== 'sortIndex' &&
      key !== 'bioNumber' &&
      key !== 'ctsByAssay' &&
      key !== 'measuredByGene',
  );
}

export function getNumDetectedTotal(datum: BarDatum) {
  return sum(getBarDatumKeys(datum).map(key => +(datum[key] ?? 0)));
}

export function isExtraWideProject(samplesByUID: FullSamplesByUID) {
  const numSamples = keys(samplesByUID).length;
  const numSamplesTreshold = 70; // 70th sample triggers fourth sample leegend column
  return numSamples >= numSamplesTreshold;
}

function getBarAndBoxDataForCoincidingSamplesByEnv(
  maybeFocusedSamples: FullSample[],
  normalisationMode: NormalisationMode,
  dateByUID: Dictionary<string>,
  grouping: GeneGrouping,
  geneGroups: string[],
  showTraces: boolean,
  useEstimate: boolean,
  fallbackToEstimate: boolean,
  envColorById: Dictionary<string>,
  envIdOrder: number[],
  projectId: number,
  getGroup: GetGroup,
) {
  // Combine sample numbers if many exist for the same env and time.
  const groupedByEnv = groupBy(maybeFocusedSamples, sample => sample.environment.id);
  const envIds = keys(groupedByEnv);
  const sortedEnvIds = sortBy(envIds, id => envIdOrder.indexOf(parseInt(id, 10)));
  return sortedEnvIds.reduce<{ boxData: BoxDatum[]; barData: BarDatum[]; heatData: BarDatum[][] }>(
    (soFar, envId) => {
      const envSamples = groupedByEnv[envId];
      const sample = envSamples[0];
      const environment = sample.environment;
      const focusedAbundances = flattenRelevantAbundances(envSamples, grouping === sixteenS); // sixteenS check is needed in case samples aren't focused yet
      const sampleUIDs = uniq(envSamples.map(sampleLocal => getBioRepGroupKey(sampleLocal)));

      const firstBioRepKey = sampleUIDs[0];
      const time = dateByUID[firstBioRepKey] && new Date(dateByUID[firstBioRepKey]).toISOString();

      return {
        boxData: [
          ...soFar.boxData,
          buildBoxDatum(
            environment,
            focusedAbundances,
            normalisationMode,
            envColorById,
            time,
            String(sample.number),
            projectId,
            sample,
            getGroup,
          ),
        ],
        barData: [
          ...soFar.barData,
          buildBarDatum(
            environment,
            grouping,
            geneGroups,
            focusedAbundances,
            time,
            envColorById,
            String(sample.number),
            projectId,
            sample,
            getGroup,
          ),
        ],
        heatData: [
          ...soFar.heatData,
          [
            ...buildSingleHeatMapSeries(
              envSamples,
              grouping,
              normalisationMode,
              showTraces,
              useEstimate,
              fallbackToEstimate,
              time,
              envColorById,
              projectId,
            ),
          ],
        ],
      };
    },
    { barData: [], boxData: [], heatData: [] },
  );
}

function buildSingleHeatMapSeries(
  samples: FullSample[],
  grouping: GeneGrouping,
  normalisationMode: NormalisationMode,
  showTraces: boolean,
  useEstimate: boolean,
  fallbackToEstimate: boolean,
  time: string,
  envColorById: Dictionary<string>,
  _projectId: number,
): SingleBarPlotSeries {
  const environment = samples[0].environment;
  const sortedSamples = sortBy(samples, sample => getSimpleBioNumber(sample));
  const result = sortedSamples.map(sample => {
    const abundances = flattenRelevantAbundances([sample], grouping === sixteenS);
    const abundanceByGene = keyBy(abundances, datum => datum.gene);
    const valueByGene = mapValues(abundanceByGene, datum => {
      let baseValue: number | null;
      if (useEstimate || (fallbackToEstimate && !datum.measured)) {
        baseValue = datum.estimates?.[normalisationMode] ?? null;
      } else {
        baseValue = getNormalisedValue(datum, normalisationMode);
      }
      return showTraces && datum.traces ? TRACES_VAL : baseValue;
    });
    const measuredByGene = mapValues(abundanceByGene, datum => datum.measured);
    const ctsByAssay = mapValues(
      keyBy(abundances, a => a.assay),
      a => a.cts || [],
    );
    return {
      date: time,
      label: environment.name,
      color: envColorById[environment.id],
      environmentId: environment.id,
      bioNumber: getSimpleBioNumber(sample),
      sampleNumber: String(sample.number),
      sampleId: String(sample.id),
      ...valueByGene,
      measuredByGene,
      ctsByAssay,
    } satisfies BarDatum;
  });
  return result;
}

function buildBarDatum(
  environment: Environment,
  grouping: GeneGrouping,
  geneGroups: string[],
  abundances: Abundance[],
  time: string,
  envColorById: Dictionary<string>,
  sampleNumber: string,
  _projectId: number,
  sample: FullSample,
  getGroup: GetGroup,
): BarDatum {
  return {
    date: time,
    label: environment.name,
    color: envColorById[environment.id],
    environmentId: environment.id,
    ...geneGroups.reduce<Dictionary<string>>((acc, geneGroup) => {
      const count = uniqBy(
        abundances.filter(datum => getGroup(datum.assay, grouping) === geneGroup && !!datum.relative),
        datum => datum.gene,
      ).length;
      acc[geneGroup] = `${count}`;
      return acc;
    }, {}),
    bioNumber: getSimpleBioNumber(sample),
    sampleNumber,
    sampleId: String(sample.id),
  } satisfies BarDatum;
}

function buildBoxDatum(
  environment: Environment,
  focusedAbundances: FullAbundance[],
  normalisationMode: NormalisationMode,
  envColorById: Dictionary<string>,
  time: string,
  sampleNumber: string,
  _projectId: number,
  sample: FullSample,
  getGroup: GetGroup,
): BoxDatum {
  const data = getAbundanceLog10Stats(
    focusedAbundances,
    {
      scope: 'detected',
      mode: normalisationMode,
      targets: values(L2Targets), // Note: research view filters (or 'focuses') abundances on a higher level in query filters
    },
    getGroup,
  );

  return {
    ...data,
    date: time,
    label: environment.name,
    color: envColorById[environment.id],
    environmentId: environment.id,
    experimentalMetrics: undefined, // buildExperimentalMetrix(focusedAbundances),
    bioNumber: getSimpleBioNumber(sample),
    sampleNumber,
    sampleId: String(sample.id),
  };
}
